import { useQuery } from '@tanstack/react-query';
import { PanelRight, SlidersHorizontal } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormField } from '@/components/shared/form-field';
import { SearchInput } from '@/components/shared/search-input';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Listbox } from '@/components/ui/listbox';
import { api } from '@/lib/api';
import { cn } from '@/utils/cn';

import { MembersDirectoryStatusFilter } from './members-directory-status-filter';
import {
  countActiveFilters,
  hasActiveListFilters,
  type MembersAppFilter,
  type MembersListState,
  type MembersSort,
} from './members-list-state';

const SORT_OPTIONS: MembersSort[] = ['name', 'joinedAt', 'createdAt'];
const APP_OPTIONS: MembersAppFilter[] = ['any', 'yes', 'no'];

export const MembersDirectoryToolbar = ({
  state,
  onPatch,
  onResetFilters,
  splitView,
  onSplitViewChange,
  searchInputId,
  className,
}: {
  state: MembersListState;
  onPatch: (patch: Partial<MembersListState>) => void;
  onResetFilters: () => void;
  splitView: boolean;
  onSplitViewChange: (next: boolean) => void;
  searchInputId?: string;
  className?: string;
}) => {
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation();

  const [filtersOpen, setFiltersOpen] = useState(false);

  const activeFilterCount = countActiveFilters(state);
  const showClearFilters = hasActiveListFilters(state);

  const departments = useQuery({
    queryKey: ['people', 'departments', 'members-toolbar'],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; name: string }> }>('/people/departments?pageSize=100'),
    enabled: filtersOpen,
  });

  const units = useQuery({
    queryKey: ['people', 'department-units', 'members-toolbar', state.departmentId],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; name: string }> }>(
        `/people/department-units?pageSize=100&departmentId=${encodeURIComponent(state.departmentId ?? '')}`,
      ),
    enabled: filtersOpen && Boolean(state.departmentId),
  });

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <SearchInput
          id={searchInputId}
          value={state.q}
          onChange={(q) => onPatch({ q, page: 1 })}
          placeholder={t('members.search')}
          aria-label={t('members.search')}
          className="min-w-0 flex-1"
        />
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {showClearFilters ? (
            <Button type="button" size="sm" variant="outline" onClick={onResetFilters}>
              {t('members.filters.clear')}
            </Button>
          ) : null}

          <MembersDirectoryStatusFilter state={state} onPatch={onPatch} />

          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setFiltersOpen(true)}
            aria-label={t('members.filters.open')}
          >
            <SlidersHorizontal className="mr-2 size-4" aria-hidden />
            {t('members.filters.title')}
            {activeFilterCount > 0 ? (
              <span className="ml-1.5 inline-flex min-w-5 items-center justify-center rounded-full bg-brand px-1.5 py-0.5 text-caption font-medium text-on-brand">
                {activeFilterCount}
              </span>
            ) : null}
          </Button>

          <Button
            type="button"
            size="sm"
            variant={splitView ? 'primary' : 'outline'}
            onClick={() => onSplitViewChange(!splitView)}
            aria-pressed={splitView}
            aria-label={t('members.preview.toggle')}
            className="hidden lg:inline-flex"
          >
            <PanelRight className="mr-2 size-4" aria-hidden />
            {t('members.preview.toggle')}
          </Button>
        </div>
      </div>

      <Dialog open={filtersOpen} onOpenChange={setFiltersOpen}>
        <DialogContent size="md" closeLabel={tCommon('action.close')}>
          <DialogHeader>
            <DialogTitle>{t('members.filters.title')}</DialogTitle>
          </DialogHeader>
          <DialogBody className="overflow-visible" contentClassName="flex flex-col gap-4">
            <FormField label={t('members.filters.sort')}>
              {(field) => (
                <Listbox
                  id={field.id}
                  value={state.sort}
                  onChange={(next) => onPatch({ sort: (next as MembersSort | null) ?? 'name', page: 1 })}
                  options={SORT_OPTIONS.map((option) => ({
                    value: option,
                    label: t(`members.filters.sortOptions.${option}`),
                  }))}
                  popoverPortal="dialog"
                />
              )}
            </FormField>
            <FormField label={t('members.filters.department')}>
              {(field) => (
                <Listbox
                  id={field.id}
                  value={state.departmentId ?? ''}
                  onChange={(next) =>
                    onPatch({
                      departmentId: next ? String(next) : null,
                      unitId: null,
                      page: 1,
                      selectedId: null,
                    })
                  }
                  options={[
                    { value: '', label: t('members.filters.any') },
                    ...(departments.data?.data ?? []).map((row) => ({
                      value: row.id,
                      label: row.name,
                    })),
                  ]}
                  popoverPortal="dialog"
                />
              )}
            </FormField>
            <FormField label={t('members.filters.unit')}>
              {(field) => (
                <Listbox
                  id={field.id}
                  value={state.unitId ?? ''}
                  onChange={(next) =>
                    onPatch({ unitId: next ? String(next) : null, page: 1, selectedId: null })
                  }
                  disabled={!state.departmentId}
                  options={[
                    { value: '', label: t('members.filters.any') },
                    ...(units.data?.data ?? []).map((row) => ({
                      value: row.id,
                      label: row.name,
                    })),
                  ]}
                  popoverPortal="dialog"
                />
              )}
            </FormField>
            <FormField label={t('members.filters.appAccount')}>
              {(field) => (
                <Listbox
                  id={field.id}
                  value={state.app}
                  onChange={(next) =>
                    onPatch({
                      app: (next as MembersAppFilter | null) ?? 'any',
                      page: 1,
                      selectedId: null,
                    })
                  }
                  options={APP_OPTIONS.map((option) => ({
                    value: option,
                    label: t(`members.filters.appOptions.${option}`),
                  }))}
                  popoverPortal="dialog"
                />
              )}
            </FormField>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setFiltersOpen(false)}>
              {tCommon('action.close')}
            </Button>
            {showClearFilters ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  onResetFilters();
                  setFiltersOpen(false);
                }}
              >
                {t('members.filters.clear')}
              </Button>
            ) : null}
            <Button
              type="button"
              onClick={() => {
                onPatch({ page: 1, selectedId: null });
                setFiltersOpen(false);
              }}
            >
              {t('members.filters.apply')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
